import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { BarChart3, ShieldCheck, Zap, Mail, CheckCircle } from 'lucide-react';
import { Label } from '../components/ui/label';

export default function ForMyClientsPage() {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate form submission for now
    setTimeout(() => {
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      setIsLoading(false);
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    }, 1000);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="bg-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="section-headline text-slate-900 mb-6">
            Elevate Your Practice with <span className="text-blue-500">WeSonder</span>
          </h1>
          <p className="body-text text-slate-600 max-w-3xl mx-auto">
            Empower your clients with objective, data-driven insights into their communication patterns and relationship dynamics. WeSonder is the perfect tool to supplement your professional practice.
          </p>
        </div>

        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="text-center">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-7 h-7 text-blue-600" />
            </div>
            <h3 className="feature-title text-slate-900 mb-2">Objective Data</h3>
            <p className="text-slate-600">Move beyond self-reported information with real communication data.</p>
          </div>
          <div className="text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Zap className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="feature-title text-slate-900 mb-2">Accelerate Progress</h3>
            <p className="text-slate-600">Help clients identify patterns faster and focus on what matters.</p>
          </div>
          <div className="text-center">
            <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-7 h-7 text-teal-600" />
            </div>
            <h3 className="feature-title text-slate-900 mb-2">Empower Clients</h3>
            <p className="text-slate-600">Provide your clients with a tool for self-reflection between sessions.</p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-2xl mx-auto">
          <Card className="border-slate-200 shadow-lg">
            <CardContent className="p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="feature-title text-slate-900 mb-2">Thank You!</h3>
                  <p className="body-text text-slate-600">We've received your message and will be in touch shortly.</p>
                </div>
              ) : (
                <>
                  <h2 className="feature-title text-slate-900 text-center mb-2">Partner with Us</h2>
                  <p className="body-text text-slate-600 text-center mb-8">
                    Interested in using WeSonder with your clients? Fill out the form below to start the conversation.
                  </p>
                  <form 
                    onSubmit={handleSubmit} 
                    className="space-y-6"
                  >
                    <div>
                      <Label htmlFor="name" className="body-text">Full Name</Label>
                      <Input 
                        id="name" 
                        name="name"
                        type="text" 
                        placeholder="Dr. Jane Doe" 
                        required 
                        className="mt-2"
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="body-text">Email Address</Label>
                      <Input 
                        id="email" 
                        name="email"
                        type="email" 
                        placeholder="you@yourpractice.com" 
                        required 
                        className="mt-2"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="message" className="body-text">Your Message</Label>
                      <Textarea 
                        id="message" 
                        name="message"
                        placeholder="Tell us a bit about your practice and how you'd like to use WeSonder..." 
                        required 
                        className="mt-2" 
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 text-base"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        'Sending...'
                      ) : (
                        <>
                          <Mail className="w-5 h-5 mr-2" />
                          Send Inquiry
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 